import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import { IOption } from "Dashboard/domain/IOption";
import { SearchType } from "Dashboard/domain/SearchType";
import { IGetInputsRequestParams } from "Dashboard/scenes/Consumables/domain/IGetInputsRequestParams";
import { IInputCreate } from "Dashboard/scenes/Consumables/domain/IInputCreate";
import { selectState as selectStateAuth } from "authentication/main.slice";
import Popover from "components/Popover/Popover";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Control,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue,
  useForm
} from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectState,
  setNewInputPopover,
  setSearchInputPopover,
} from "../input.slice";
import NewInputPopover from "./NewInputPopover";
import PrintInput from "./PrintInput";
import SearchInputPopover from "./SearchInputPopover";
import { CONSUMABLES_MODULE_NAME } from "Dashboard/scenes/Consumables/domain/ConsumablesModule";
import { hasWriteAccessTo } from "utils/authUtil";
import { usePagePrint } from "hooks/usePagePrint";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { useLocalStorage } from "hooks/LocalStorage";
import { useAppDispatch } from "hooks/hooks";

interface IProps {
  defaultGetInputsRequestParams: IGetInputsRequestParams;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetInputsRequestParams>>;
  registerSearch: UseFormRegister<IGetInputsRequestParams>;
  handleSubmitSearch: UseFormHandleSubmit<IGetInputsRequestParams>;
  resetSearch: UseFormReset<IGetInputsRequestParams>;
  controlSearch: Control<IGetInputsRequestParams, any>;
  getValuesSearch: UseFormGetValues<IGetInputsRequestParams>;
  setFocusSearch: UseFormSetFocus<IGetInputsRequestParams>;
  setValueSearch: UseFormSetValue<IGetInputsRequestParams>;
};

const Toolbox = ({
  defaultGetInputsRequestParams,
  setGetInputsRequestParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  getValuesSearch,
  setFocusSearch,
  setValueSearch,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const inputComponentRef = useRef<HTMLDivElement>(null);
  const { newInputPopover, searchInputPopover } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const {
    handleSubmit,
    setValue,
    register,
    control,
    getValues,
    formState: { errors },
    reset,
  } = useForm<IInputCreate>();
  const [knownItemCodes, setKnownItemCodes] = useState<string[]>([]);
  const [supplierValue, setSupplierValue]
    = useState<IOption | undefined>(undefined);

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("consumablesSearchTerm", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("consumablesSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("consumablesSearchInputs", null);

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch());

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetInputsRequestParams({ searchTerm: storedSearchTerm });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchTerm = undefined;
      setGetInputsRequestParams({ ...storedSearchInputs });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchTerm", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchTypeValue(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchTerm") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    if (path === '/dashboard') {
      localStorage.removeItem("consumablesSearchTerm");
      localStorage.removeItem("consumablesSearchType");
      localStorage.removeItem("consumablesSearchInputs");
      localStorage.removeItem("consumablesOutputSearchQuery");
      localStorage.removeItem("consumablesOutputSearchType");
      localStorage.removeItem("consumablesOutputSearchInputs");
      resetSearch(defaultGetInputsRequestParams);
    }
    else {
      const currentSearchTerm = getValuesSearch("searchTerm");
      const currentSearchInputs = getValuesSearch();
      setStoredSearchTerm(currentSearchTerm);
      setStoredSearchType(searchTypeValue);
      setStoredSearchInputs(currentSearchInputs);
    }
    navigate(path);
  };

  const handlePrintInput = usePagePrint(
    inputComponentRef, { margins: "0.5cm" });

  const handleResetSearch = () => {
    resetSearch(defaultGetInputsRequestParams);
    setGetInputsRequestParams(defaultGetInputsRequestParams);
  };

  return (
    <>
      <nav className="container nav-buttons">
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Back}
            tooltip="Glavni meni"
            toolbox={true}
            onClick={() => handleNavigation("/dashboard")}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Forward}
            tooltip="Potrošni materijali - Izlaz"
            toolbox={true}
            onClick={() => handleNavigation("/consumables/output")}
          />
        </div>
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Add}
            tooltip="Novi ulaz"
            toolbox={true}
            onClick={hasWriteAccessTo(user, CONSUMABLES_MODULE_NAME)
              ? () => dispatch(setNewInputPopover({ isOpen: true }))
              : undefined}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() =>
              dispatch(setSearchInputPopover({ isOpen: true }))}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => handleResetSearch()}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampaj sve"
            toolbox={true}
            onClick={handlePrintInput}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampaj obostrano"
            toolbox={true}
          />
          <div className="print-area">
            <div ref={inputComponentRef}>
              <PrintInput />
            </div>
          </div>
        </div>
      </nav>
      <Popover
        isOpen={newInputPopover.isOpen}
        onClose={() => dispatch(setNewInputPopover({ isOpen: false }))}
        title="Dodaj materijal"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 250 }}
      >
        <NewInputPopover
          handleSubmit={handleSubmit}
          setValue={setValue}
          register={register}
          control={control}
          getValues={getValues}
          errors={errors}
          reset={reset}
          knownItemCodes={knownItemCodes}
          setKnownItemCodes={setKnownItemCodes}
          supplierValue={supplierValue}
          setSupplierValue={setSupplierValue}
        />
      </Popover>
      <Popover
        isOpen={searchInputPopover.isOpen}
        onClose={() => {
          dispatch(setSearchInputPopover({ isOpen: false }));
        }}
        title="Pretraga"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 310 }}
      >
        <SearchInputPopover
          handleSubmit={handleSubmitSearch}
          register={registerSearch}
          control={controlSearch}
          getValues={getValuesSearch}
          reset={resetSearch}
          setFocus={setFocusSearch}
          searchTypeValue={searchTypeValue}
          setSearchTypeValue={setSearchTypeValue}
          setValue={setValueSearch}
          setGetInputsRequestParams={setGetInputsRequestParams}
        />
      </Popover>
    </>
  );
};

export default Toolbox;
